import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, Hash, IdCard, Loader2, Copy, CheckCircle2, Sparkles, ExternalLink, Download, QrCode, ArrowUpRight, CreditCard, IndianRupee } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { useIdempotentPost } from "@/hooks/useIdempotentPost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSearchParams, Link, useLocation, useNavigate } from "react-router-dom";

const rupees = (paise) => `₹${(paise / 100).toFixed(2)}`;

/**
 * Consolidated hub for the three new modules: Wallet, NVN, Business Card.
 * Tabs are lightweight (URL-hash based) so users can deep-link into a specific section.
 */
export default function Hub() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState(window.location.hash.replace("#", "") || "wallet");

  useEffect(() => {
    const onHash = () => setTab(window.location.hash.replace("#", "") || "wallet");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Voice intent hand-off (see VoiceSearchMic.jsx). "Top up 500 rupees" navigates
  // here with { state: { voice_intent: { action: "topup", amount } } } — we scroll
  // the topup card into view and toast a friendly reminder to pick a package.
  // We never auto-execute a payment; the user must tap a package explicitly.
  const routerLocation = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const intent = routerLocation.state?.voice_intent;
    if (!intent) return;
    if (intent.action === "topup") {
      setTab("wallet");
      setTimeout(() => {
        document.querySelector('[data-voice-target="topup"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      toast.info(intent.amount
        ? `Pick a top-up close to ₹${intent.amount} — packages below.`
        : "Choose a top-up package below to add money.");
    }
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [routerLocation.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (t) => { window.location.hash = t; setTab(t); };

  if (authLoading) return <div className="p-12 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>;
  if (!user) return (
    <div className="max-w-lg mx-auto p-12 text-center">
      <div className="text-lg font-bold mb-2">Sign in to access your hub</div>
      <Link to="/"><Button className="rounded-full">Go home</Button></Link>
    </div>
  );

  const TABS = [
    { key: "wallet", label: "Wallet", icon: Wallet },
    { key: "nvn", label: "NVN", icon: Hash },
    { key: "card", label: "Business card", icon: IdCard },
  ];

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-6">
      <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mb-1">Your hub</h1>
      <p className="text-stone-500 text-sm mb-5">Wallet, NVN and digital business card — all in one place.</p>

      <div className="flex gap-2 mb-6 border-b border-stone-200 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${active ? "text-[#E05E36] border-[#E05E36]" : "text-stone-500 border-transparent hover:text-stone-800"}`}
              data-testid={`hub-tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "wallet" && <WalletTab />}
      {tab === "nvn" && <NvnTab />}
      {tab === "card" && <CardTab user={user} />}
    </div>
  );
}

// ---------------- Wallet ----------------
function WalletTab() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [packages, setPackages] = useState([]);
  const [buying, setBuying] = useState(null);
  const [params] = useSearchParams();

  const refresh = () => {
    api.get("/wallet").then(r => setWallet(r.data)).catch(() => {});
    api.get("/wallet/transactions").then(r => setTxns(r.data)).catch(() => {});
    api.get("/wallet/packages").then(r => setPackages(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  // Poll payment status if we came back from Stripe success.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Wallet topped up ✅");
          refresh();
          clearInterval(iv);
        } else if (attempts >= 15) {
          clearInterval(iv);
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [params]);

  const postOnce = useIdempotentPost("tu");
  const buy = async (packageId) => {
    setBuying(packageId);
    try {
      const r = await postOnce("/wallet/topup", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
      setBuying(null);
    }
  };

  if (!wallet) return <div className="p-8 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="hub-wallet">
      {/* Balance card */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-[#2D5A27] to-[#1a3e17] text-white rounded-3xl p-6 md:p-7">
        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-200 mb-1">Total available balance</div>
        <div className="text-4xl md:text-5xl font-black tracking-tight" data-testid="wallet-balance">
          {rupees(wallet.total_available_paise)}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
          <div><div className="text-emerald-200/80">Free credit</div><div className="font-bold text-lg">{rupees(wallet.free_credit_paise)}</div></div>
          <div><div className="text-emerald-200/80">Topped up</div><div className="font-bold text-lg">{rupees(wallet.balance_paise)}</div></div>
        </div>
      </motion.div>

      {/* Top-up packages */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-voice-target="topup">
        <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
          <CreditCard size={14} className="text-[#E05E36]" />
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Top up your wallet</div>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {packages.map(p => (
            <button
              key={p.package_id}
              onClick={() => buy(p.package_id)}
              disabled={!!buying}
              className="text-left rounded-2xl border-2 border-stone-200 hover:border-[#E05E36] hover:shadow-md p-4 transition-all disabled:opacity-60"
              data-testid={`hub-topup-${p.package_id}`}
            >
              <div className="font-black text-2xl text-stone-900">₹{p.amount}</div>
              <div className="text-xs text-stone-500 mt-0.5">{p.label.split("·")[1]?.trim() || "actions"}</div>
              {buying === p.package_id && <Loader2 size={14} className="animate-spin mt-2 text-[#E05E36]" />}
            </button>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-stone-100 text-[11px] text-stone-500">
          You pay ₹1 per action on Find2Near (view contact, upload catalogue photo). Rs 50 is free on signup.
        </div>
      </div>

      {/* Postpaid mode toggle — business accounts only */}
      {wallet.postpaid_eligible && (
        <PostpaidPanel wallet={wallet} onToggle={refresh} />
      )}

      {/* Transactions */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
          <ArrowUpRight size={14} className="text-stone-700" />
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Recent activity ({txns.length})</div>
        </div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-xs text-stone-500">No transactions yet.</div>
        ) : (
          <div className="divide-y divide-stone-100" data-testid="hub-wallet-txns">
            {txns.slice(0, 15).map(t => (
              <div key={t.txn_id} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                <span className={`inline-block w-2 h-2 rounded-full ${t.direction === "debit" ? "bg-red-500" : "bg-emerald-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 capitalize truncate">{t.kind.replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-mono text-sm font-bold ${t.direction === "debit" ? "text-red-600" : "text-emerald-700"}`}>
                  {t.direction === "debit" ? "-" : "+"}{rupees(t.amount_paise)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- NVN ----------------
function NvnTab() {
  const [nvn, setNvn] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [fancy, setFancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get("/nvn/me")
      .then(r => setNvn(r.data))
      .catch(() => setNvn(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const getCandidates = async () => {
    try {
      const r = await api.get("/nvn/candidates");
      setCandidates(r.data.candidates);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch NVN candidates");
    }
  };

  const claim = async (pick) => {
    setClaiming(pick);
    try {
      await api.post("/nvn/claim", { nvn: pick });
      toast.success(`NVN ${pick} claimed`);
      setCandidates([]);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(null);
    }
  };

  const loadFancy = async () => {
    try {
      const r = await api.get("/nvn/fancy/available");
      setFancy(r.data);
    } catch (_) { /* silent */ }
  };

  const reserveFancy = async (num) => {
    if (!window.confirm(`Reserve fancy NVN ${num} for ₹${fancy?.price_inr}? Wallet will be debited.`)) return;
    setClaiming(num);
    try {
      await api.post("/nvn/reserve-fancy", { nvn: num });
      toast.success(`Fancy NVN ${num} reserved ✨`);
      refresh(); setFancy(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reservation failed";
      if (msg.includes("insufficient")) {
        toast.error("Not enough wallet balance. Top up first."); window.location.hash = "wallet";
      } else {
        toast.error(msg);
      }
    } finally {
      setClaiming(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="hub-nvn">
      {nvn ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-stone-900 via-stone-800 to-[#2D5A27] text-white rounded-3xl p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-200 mb-1 flex items-center gap-1.5">
            <Sparkles size={12} /> Your Near Vocal Number
          </div>
          <div className="font-mono text-3xl md:text-5xl font-black tracking-widest my-2" data-testid="hub-nvn-value">
            {nvn.nvn.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4")}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {nvn.is_fancy && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-500/20 text-amber-200 border border-amber-400/30 rounded-full px-2 py-0.5"><Sparkles size={10} /> Fancy</span>}
            <button
              onClick={() => { navigator.clipboard.writeText(nvn.nvn); toast.success("NVN copied"); }}
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-200 hover:text-white"
              data-testid="hub-nvn-copy"
            >
              <Copy size={12} /> Copy
            </button>
            <Link to={`/card/${nvn.nvn}`} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-200 hover:text-white ml-auto">
              View my card <ExternalLink size={12} />
            </Link>
          </div>
          <div className="text-[11px] text-emerald-200/70 mt-3">
            Share this instead of your phone number — it works online, via Bluetooth/WiFi handshake, or as a spoken code.
          </div>
        </motion.div>
      ) : candidates.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-stone-300 rounded-3xl p-8 text-center">
          <Hash size={40} className="mx-auto text-stone-300 mb-3" />
          <h3 className="heading text-2xl font-extrabold text-stone-900 mb-1">Claim your Near Vocal Number</h3>
          <p className="text-stone-500 text-sm mb-5">A 12-digit universal contact ID that works everywhere — no SIM needed.</p>
          <Button onClick={getCandidates} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="hub-nvn-get-candidates">
            Show me 10 options
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-3">Pick your NVN (any one — it&apos;s free)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="hub-nvn-candidates">
            {candidates.map(c => (
              <button
                key={c}
                onClick={() => claim(c)}
                disabled={!!claiming}
                className="font-mono text-sm font-bold py-3 rounded-xl border-2 border-stone-200 hover:border-[#E05E36] hover:bg-[#E05E36]/5 transition-colors disabled:opacity-50"
                data-testid={`hub-nvn-claim-${c}`}
              >
                {claiming === c ? <Loader2 size={14} className="animate-spin mx-auto" /> : c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fancy NVN */}
      {nvn && !nvn.is_fancy && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5">
                <Sparkles size={12} className="text-amber-500" /> Want a fancy NVN?
              </div>
              <div className="text-xs text-stone-500 mt-1">Memorable numbers like 111 111 111 111 or 150 000 000 000. ₹50,000 one-time.</div>
            </div>
            {!fancy && <Button onClick={loadFancy} variant="outline" className="rounded-full" data-testid="hub-nvn-load-fancy">See fancy numbers</Button>}
          </div>
          {fancy && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="hub-nvn-fancy-list">
              {fancy.fancy_numbers.slice(0, 12).map(f => (
                <button
                  key={f}
                  onClick={() => reserveFancy(f)}
                  disabled={!!claiming}
                  className="font-mono text-sm font-bold py-3 rounded-xl border-2 border-amber-200 bg-amber-50 hover:border-amber-500 disabled:opacity-50"
                  data-testid={`hub-nvn-fancy-${f}`}
                >
                  {claiming === f ? <Loader2 size={14} className="animate-spin mx-auto" /> : f}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Business Card ----------------
function CardTab({ user }) {
  const [card, setCard] = useState({
    bio: "", tagline: "", linkedin_url: "", twitter_url: "", instagram_url: "", facebook_url: "",
    justdial_url: "", google_business_url: "", catalogue_url: "", website_url: "", address: "", whatsapp_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [nvn, setNvn] = useState(null);

  useEffect(() => {
    api.get(`/cards/${user.user_id}`).then(r => {
      setCard(c => ({ ...c, ...(r.data.card || {}) }));
      setNvn(r.data.nvn);
    }).catch(() => {});
  }, [user.user_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/card", card);
      toast.success("Card saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cardKey = nvn || user.user_id;
  const publicUrl = `${window.location.origin}/card/${cardKey}`;

  return (
    <div className="space-y-5" data-testid="hub-card">
      {/* Preview + share */}
      <div className="bg-gradient-to-br from-white to-stone-50 border border-stone-200 rounded-3xl p-5 md:p-6 flex flex-col md:flex-row items-start gap-5">
        <div className="w-32 h-32 shrink-0 rounded-2xl border-4 border-stone-900 p-2 bg-white">
          <img loading="lazy" src={`${API}/cards/${cardKey}/qr.svg`} alt="Card QR" className="w-full h-full" data-testid="hub-card-qr" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#2D5A27]">Your public business card</div>
          <div className="heading text-2xl font-extrabold text-stone-900 mt-1 truncate">{user.name}</div>
          {nvn && <div className="font-mono text-xs text-stone-500 mt-0.5">NVN {nvn}</div>}
          <div className="text-xs text-stone-500 font-mono truncate mt-2">{publicUrl}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} className="rounded-full text-xs h-8" data-testid="hub-card-copy">
              <Copy size={12} className="mr-1.5" /> Copy link
            </Button>
            <a href={`${API}/cards/${cardKey}.vcf`} download className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 hover:border-[#E05E36] text-xs font-bold text-stone-800 px-3 h-8" data-testid="hub-card-vcf">
              <Download size={12} /> Download vCard
            </a>
            <a href={`${API}/cards/${cardKey}/qr.svg`} download className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 hover:border-[#E05E36] text-xs font-bold text-stone-800 px-3 h-8" data-testid="hub-card-qr-download">
              <QrCode size={12} /> Download QR
            </a>
          </div>
          <div className="text-[10px] text-stone-400 mt-2 leading-relaxed">
            Import the vCard into your phone contacts, or write it to a physical NFC tag with the free &quot;NFC Tools&quot; app — tap phone → your card opens.
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 bg-stone-50 border-b border-stone-200">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Card details</div>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4">
          <Field label="Tagline" val={card.tagline} onChange={v => setCard({ ...card, tagline: v })} placeholder="e.g. Best kulhad chai in Delhi" testid="card-tagline" />
          <Field label="Address" val={card.address} onChange={v => setCard({ ...card, address: v })} placeholder="Shop address (public)" testid="card-address" />
          <Field label="WhatsApp number" val={card.whatsapp_number} onChange={v => setCard({ ...card, whatsapp_number: v })} placeholder="10-digit mobile" testid="card-wa" />
          <Field label="Website" val={card.website_url} onChange={v => setCard({ ...card, website_url: v })} placeholder="https://…" testid="card-web" />
          <Field label="Catalogue URL" val={card.catalogue_url} onChange={v => setCard({ ...card, catalogue_url: v })} placeholder="Google Drive, Notion, PDF…" testid="card-cat" />
          <Field label="LinkedIn" val={card.linkedin_url} onChange={v => setCard({ ...card, linkedin_url: v })} placeholder="linkedin.com/in/…" testid="card-in" />
          <Field label="Instagram" val={card.instagram_url} onChange={v => setCard({ ...card, instagram_url: v })} placeholder="instagram.com/…" testid="card-ig" />
          <Field label="Facebook" val={card.facebook_url} onChange={v => setCard({ ...card, facebook_url: v })} placeholder="facebook.com/…" testid="card-fb" />
          <Field label="Twitter / X" val={card.twitter_url} onChange={v => setCard({ ...card, twitter_url: v })} placeholder="x.com/…" testid="card-tw" />
          <Field label="Justdial listing" val={card.justdial_url} onChange={v => setCard({ ...card, justdial_url: v })} placeholder="justdial.com/…" testid="card-jd" />
          <Field label="Google business" val={card.google_business_url} onChange={v => setCard({ ...card, google_business_url: v })} placeholder="g.page/…" testid="card-gb" />
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Bio</label>
            <Textarea value={card.bio || ""} onChange={e => setCard({ ...card, bio: e.target.value })} rows={3} className="mt-1.5 rounded-xl" data-testid="card-bio" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="hub-card-save">
            {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</> : <><CheckCircle2 size={14} className="mr-1.5" /> Save card</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, val, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</label>
      <Input value={val || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5 rounded-xl" data-testid={testid} />
    </div>
  );
}

function PostpaidPanel({ wallet, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [invoice, setInvoice] = useState(null);
  // Optimistic local mirror of the server flag — flips immediately on click
  // so the switch feels responsive instead of "stuck" while /wallet refetches.
  const [enabled, setEnabled] = useState(!!wallet.postpaid_mode);

  // Keep local state in sync when the parent's wallet prop refreshes.
  useEffect(() => { setEnabled(!!wallet.postpaid_mode); }, [wallet.postpaid_mode]);

  useEffect(() => {
    if (enabled) {
      api.get("/wallet/invoice").then(r => setInvoice(r.data)).catch(() => {});
    } else {
      setInvoice(null);   // clear cached invoice when disabled
    }
  }, [enabled]);

  const toggle = async () => {
    if (busy) return;
    const next = !enabled;
    setEnabled(next);          // optimistic flip
    setBusy(true);
    try {
      await api.patch("/wallet/postpaid", { enabled: next });
      toast.success(next ? "Postpaid enabled — you can now use Find2Near with a monthly invoice" : "Postpaid disabled");
      onToggle();
    } catch (e) {
      setEnabled(!next);       // rollback on failure
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="hub-postpaid-panel">
      <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Business postpaid mode</div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle business postpaid mode"
          onClick={toggle}
          disabled={busy}
          className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 ${enabled ? "bg-emerald-500" : "bg-stone-300"}`}
          data-testid="hub-postpaid-toggle"
        >
          <span
            className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform duration-200 ${enabled ? "translate-x-6" : "translate-x-0"}`}
          />
        </button>
      </div>
      <div className="p-5 text-xs text-stone-600 leading-relaxed">
        Use Find2Near actions freely all month, get one consolidated invoice at month-end. Perfect for busy business accounts.
        {enabled && invoice != null && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <div className="font-bold text-amber-900 flex items-center gap-1.5"><IndianRupee size={12} /> Current invoice: {rupees(invoice.invoice_pending_paise)}</div>
            <div className="text-[11px] text-amber-800 mt-1">{invoice.line_items?.length || 0} line items · settled at end of billing cycle.</div>
          </div>
        )}
      </div>
    </div>
  );
}
